import { Link } from "react-router-dom";
import { ArrowUpRight, Layers } from "lucide-react";
import CTAButton from "@/components/CTAButton";
import {
  PRIMARY_SECTORS, SECONDARY_SECTORS, RISK_WATCH_SOURCES, FUTURE_CAPABILITIES, DISCLAIMER_TEXT,
} from "@/lib/constants";

const Wrap = ({ id, children, className = "" }) => (
  <section id={id} className={`border-b border-white/[0.08] ${className}`}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">{children}</div>
  </section>
);

export function PortfolioTeaser() {
  return (
    <Wrap id="portfolio" className="bg-[#0C0E13]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="kicker mb-4 flex items-center gap-3"><Layers className="w-3.5 h-3.5 text-emerald-400" /> Portfolio Screening</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="portfolio-teaser-title">Reviewing multiple sites?</h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-2xl">
            For developers, landowners and advisers evaluating several opportunities, PFAS Risk Intelligence can provide portfolio-level source screening to help identify which sites warrant deeper review first.
          </p>
          <p className="mt-4 text-white leading-relaxed max-w-2xl">
            Prioritise the sites that need attention before committing full investigation spend.
          </p>
        </div>
        <div className="lg:col-span-5 flex lg:justify-end">
          <CTAButton to="/portfolio-screening" primary testId="portfolio-teaser-cta">Discuss Portfolio Screening</CTAButton>
        </div>
      </div>
    </Wrap>
  );
}

export function Sectors() {
  return (
    <Wrap id="sectors">
      <div className="kicker mb-4">Sector relevance</div>
      <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
        Built for land and development decisions.
      </h2>
      <div className="mt-12">
        <div className="text-[11px] font-mono-alt tracking-[0.18em] uppercase text-emerald-400 mb-4">Primary</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.08]">
          {PRIMARY_SECTORS.map((s) => (
            <div key={s.t} className="bg-[#0A0B0E] p-6 hover-lift hover:bg-[#12141A]" data-testid={`sector-primary-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="font-display text-xl leading-snug">{s.t}</div>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10">
        <div className="text-[11px] font-mono-alt tracking-[0.18em] uppercase text-white/40 mb-4">Also relevant to</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.08]">
          {SECONDARY_SECTORS.map((s) => (
            <div key={s.t} className="bg-[#0A0B0E] p-6" data-testid={`sector-secondary-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="font-display text-lg leading-snug text-white/85">{s.t}</div>
              {s.h && <p className="mt-2 text-sm text-white/75 leading-snug">{s.h}</p>}
              <p className="mt-3 text-xs text-white/50 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </Wrap>
  );
}

export function RiskWatchTeaser() {
  return (
    <Wrap id="risk-watch">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="kicker mb-4">PFAS Risk Watch · Free</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="risk-watch-teaser-title">
            A curated UK PFAS land-risk intelligence service.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            Recent PFAS developments from credible UK sources, reviewed and summarised for what changed, why it matters, who may be affected and what it could mean for planning, acquisition, development, lending, insurance, remediation or legal strategy.
          </p>
          <div className="mt-8">
            <CTAButton to="/risk-watch" testId="risk-watch-teaser-cta">Subscribe to PFAS Risk Watch</CTAButton>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="kicker mb-4">Sources we track</div>
          <div className="flex flex-wrap gap-2">
            {RISK_WATCH_SOURCES.map((s) => (
              <span key={s} className="border border-white/15 px-3 py-1.5 text-[11px] font-mono-alt tracking-[0.12em] uppercase text-white/70">{s}</span>
            ))}
          </div>
          <div className="mt-10 border border-white/10 bg-[#0C0E13] p-6">
            <div className="kicker">Developing capability</div>
            <p className="mt-3 text-sm text-white/70 leading-relaxed">
              We are building a structured intelligence picture of UK PFAS source activity, known investigations and emerging land-risk evidence.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
              {FUTURE_CAPABILITIES.map((c) => (
                <span key={c} className="text-xs text-white/45">▸ {c}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Wrap>
  );
}

export function MethodLimitations() {
  return (
    <Wrap id="method">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="kicker mb-4">Method · Evidence · Limitations</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">Preliminary by design. Transparent about what it is not.</h2>
        </div>
        <div className="lg:col-span-8 space-y-6 text-white/65 leading-relaxed">
          <p>
            Our review draws on publicly available and client-supplied evidence: historic mapping and land-use records, planning history, regulatory registers, published investigations, hydrogeological context and PFAS-relevant activity records. Where evidence is limited or unverified, we say so and record it as an evidence gap.
          </p>
          <p>
            The objective is not to declare PFAS present. It is to identify whether the available evidence justifies further investigation — and, where it does, what the proportionate next step looks like.
          </p>
          <p className="text-sm text-white/50 border-l border-white/15 pl-5" data-testid="method-disclaimer">
            {DISCLAIMER_TEXT}
          </p>
          <Link to="/disclaimer" data-testid="method-disclaimer-link" className="inline-flex items-center gap-2 text-[12px] font-mono-alt tracking-[0.18em] uppercase text-white group">
            Read the full disclaimer <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </Wrap>
  );
}
